/**
 * Launch step of the batch workspace (UX plan §4.5) — closes D3/D10.
 *
 * 1. Winners strip: auto-named via the project naming convention
 *    (project-level setting + one-off batch override, decision #4).
 * 2. Destination & tracking: exact per-locale Segmently destination + auto-UTM
 *    (utm_campaign from batch, utm_content = ad name). Launch cannot replace
 *    the destination identity with an arbitrary URL.
 * 3. Send to Meta: paused ad set is PRIMARY (decision #3) — one new paused ad
 *    set + a paused ad per winner; Meta bulk sheet is the secondary export.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeBrief, AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BATCH } from '../../selectors';
import type {
  AdCreativeApiClient,
  MetaAdsAdAccountSummary,
  MetaAdsAdSetSummary,
  MetaAdsCampaignSummary,
  MetaAdsConnectionStatus,
  MetaAdsPageSummary,
} from '../../services/AdCreativeApiClient';
import {
  formatBadge,
  getBriefAngles,
  hookLine,
} from './batchModel';
import {
  findCreativeLocaleKey,
  getCreativeLocaleStatus,
  getExactCreativeLocaleCopy,
  isSegmentlyOwnedCreativeItem,
  resolveCreativeLocaleProjection,
} from '../../services/creativeLocaleProjection';
import {
  appendUtmToUrl,
  buildUtm,
  DEFAULT_NAMING_CONVENTION,
  renderAdName,
  slugToken,
} from './naming';

type AdSetMode = 'new' | 'existing';

interface BatchLaunchStepProps {
  projectId: string;
  client: AdCreativeApiClient;
  brief: AdCreativeBrief;
  winners: AdCreativeItem[];
  allItems: AdCreativeItem[];
  onItemsUpdated: () => void;
}

export function BatchLaunchStep({
  projectId,
  client,
  brief,
  winners,
  allItems,
  onItemsUpdated,
}: BatchLaunchStepProps): React.ReactElement {
  const [namingTemplate, setNamingTemplate] = useState(DEFAULT_NAMING_CONVENTION);
  const [projectNaming, setProjectNaming] = useState<string | null>(null);
  const [savingNaming, setSavingNaming] = useState(false);
  const [metaStatus, setMetaStatus] = useState<MetaAdsConnectionStatus | null>(null);
  const [accounts, setAccounts] = useState<MetaAdsAdAccountSummary[]>([]);
  const [pages, setPages] = useState<MetaAdsPageSummary[]>([]);
  const [campaigns, setCampaigns] = useState<MetaAdsCampaignSummary[]>([]);
  const [adSets, setAdSets] = useState<MetaAdsAdSetSummary[]>([]);
  const [accountId, setAccountId] = useState('');
  const [pageId, setPageId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [adSetMode, setAdSetMode] = useState<AdSetMode>('new');
  const [adSetId, setAdSetId] = useState('');
  const [newAdSetName, setNewAdSetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [localesByItemId, setLocalesByItemId] = useState<Record<string, string>>({});
  const [fallbackApprovedByItemId, setFallbackApprovedByItemId] = useState<Record<string, boolean>>({});

  const angles = useMemo(() => getBriefAngles(brief), [brief]);
  const connected = Boolean(metaStatus?.connected && !metaStatus.needsReconnect);
  const utm = useMemo(() => buildUtm({ batchTitle: brief.title }), [brief.title]);
  const brandToken = useMemo(() => {
    const productContext = brief.productContext as Record<string, unknown> | undefined;
    return String(productContext?.brandName ?? brief.title ?? 'brand');
  }, [brief.productContext, brief.title]);

  useEffect(() => {
    setLocalesByItemId((current) => Object.fromEntries(winners.map((item) => {
      const currentLocale = current[item.id];
      return [item.id, findCreativeLocaleKey(item, currentLocale ?? '') ?? item.defaultLanguage];
    })));
    setFallbackApprovedByItemId((current) => Object.fromEntries(winners.map((item) => (
      [item.id, current[item.id] === true]
    ))));
  }, [winners]);

  const namedWinners = useMemo(() => winners.map((item) => {
    const locale = localesByItemId[item.id] ?? item.defaultLanguage;
    return {
      item,
      locale,
      adName: renderAdName(namingTemplate, item, { brand: brandToken, angles, locale }),
    };
  }), [angles, brandToken, localesByItemId, namingTemplate, winners]);
  const localeBlockers = useMemo(() => namedWinners.filter(({ item, locale }) => (
    !isCreativeLocaleStrictlyLaunchable(item, locale)
  )), [namedWinners]);

  useEffect(() => {
    setNewAdSetName(`${slugToken(brief.title, 32)}-batch`);
  }, [brief.title]);

  useEffect(() => {
    let cancelled = false;
    void client.getPlatformSettings(projectId, brief.networkId || 'meta').then((response) => {
      if (cancelled) return;
      const convention = response.data?.settings?.namingConvention;
      if (convention) {
        setProjectNaming(convention);
        setNamingTemplate(convention);
      }
    }).catch(() => {
      // Settings endpoint unavailable → keep the default convention.
    });
    return () => {
      cancelled = true;
    };
  }, [brief.networkId, client, projectId]);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await client.getMetaAdsStatus(projectId);
      if (status.error) throw new Error(status.error);
      setMetaStatus(status);
      if (!status.connected || status.needsReconnect) return;
      const [accountsResponse, pagesResponse] = await Promise.all([
        client.listMetaAdAccounts(projectId),
        client.listMetaPages(projectId),
      ]);
      if (accountsResponse.error) throw new Error(accountsResponse.error);
      if (pagesResponse.error) throw new Error(pagesResponse.error);
      const nextAccounts = accountsResponse.accounts ?? status.accessibleAdAccounts ?? [];
      setAccounts(nextAccounts);
      setPages(pagesResponse.pages ?? []);
      setAccountId((current) => current || nextAccounts[0]?.id || '');
      setPageId((current) => current || pagesResponse.pages?.[0]?.id || '');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLoading(false);
    }
  }, [client, projectId]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (!connected || !accountId) {
      setCampaigns([]);
      setCampaignId('');
      return;
    }
    let cancelled = false;
    void client.listMetaCampaigns(projectId, accountId).then((response) => {
      if (cancelled) return;
      if (response.error) throw new Error(response.error);
      const next = response.campaigns ?? [];
      setCampaigns(next);
      setCampaignId((current) => (current && next.some((campaign) => campaign.id === current) ? current : next[0]?.id || ''));
    }).catch((nextError: unknown) => {
      if (!cancelled) setError(nextError instanceof Error ? nextError.message : String(nextError));
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, client, connected, projectId]);

  useEffect(() => {
    if (!connected || !accountId || !campaignId || adSetMode !== 'existing') {
      setAdSets([]);
      setAdSetId('');
      return;
    }
    let cancelled = false;
    void client.listMetaAdSets(projectId, accountId, campaignId).then((response) => {
      if (cancelled) return;
      if (response.error) throw new Error(response.error);
      const next = response.adsets ?? [];
      setAdSets(next);
      setAdSetId((current) => (current && next.some((adSet) => adSet.id === current) ? current : next[0]?.id || ''));
    }).catch((nextError: unknown) => {
      if (!cancelled) setError(nextError instanceof Error ? nextError.message : String(nextError));
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, adSetMode, campaignId, client, connected, projectId]);

  const saveNamingAsProjectDefault = async () => {
    setSavingNaming(true);
    try {
      const response = await client.updatePlatformSettings(projectId, { namingConvention: namingTemplate }, brief.networkId || 'meta');
      if (!response.success) throw new Error(response.error || 'Failed to save naming convention');
      setProjectNaming(namingTemplate);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSavingNaming(false);
    }
  };

  const submitLaunch = async () => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const response = await client.createBatchMetaLaunch({
        projectId,
        briefId: brief.id,
        itemIds: winners.map((item) => item.id),
        localesByItemId,
        adAccountId: accountId,
        pageId,
        campaignId,
        adSetId: adSetMode === 'existing' ? adSetId : undefined,
        newAdSetName: adSetMode === 'new' ? newAdSetName : undefined,
        fallbackApprovedByItemId,
        namingConvention: namingTemplate,
        utm: utm as unknown as Record<string, string>,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create the paused ad set in Meta');
      }
      const failed = response.data.errors?.length ?? 0;
      setResult(`Paused ad set ${response.data.adSetId}: ${response.data.drafts.length} ads created${failed ? `, ${failed} failed` : ''}. Nothing is spending — review in Meta Ads Manager.`);
      onItemsUpdated();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadBulkSheet = () => {
    const csv = buildBulkSheetCsv(namedWinners.map(({ item, adName, locale }) => {
      const copy = getExactCreativeLocaleCopy(item, locale);
      const projection = resolveCreativeLocaleProjection(item, locale, 'strict');
      return {
      adName,
      campaignName: campaigns.find((campaign) => campaign.id === campaignId)?.name ?? '',
      adSetName: adSetMode === 'new' ? newAdSetName : adSets.find((adSet) => adSet.id === adSetId)?.name ?? '',
      headline: copy.headline ?? '',
      primaryText: copy.primaryText ?? '',
      description: copy.description ?? '',
      cta: copy.cta ?? '',
      landingUrl: appendUtmToUrl(
        String(item.localizedDestinations?.[locale]?.url ?? ''),
        utm as unknown as Record<string, string>,
      ),
      mediaUrl: projection.previewUrl ?? '',
      format: formatBadge(item),
      locale,
    };
    }));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugToken(brief.title, 40)}-meta-bulk-sheet.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (winners.length === 0) {
    return (
      <Alert severity="info" data-testid={AD_CREATIVE_BATCH.launchStep}>
        Pick winners in the Creatives step first — the launch sends only checked creatives.
        {allItems.length === 0 ? ' This batch has no creatives yet.' : ''}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} data-testid={AD_CREATIVE_BATCH.launchStep}>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {`Winners (${winners.length})`}
        </Typography>
        <Stack spacing={1} data-testid={AD_CREATIVE_BATCH.launchWinnersStrip}>
          {namedWinners.map(({ item, adName, locale }) => (
            <Stack key={item.id} direction="row" spacing={1.5} alignItems="center">
              <PreviewThumb item={item} locale={locale} />
              <Stack sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace' }}
                  noWrap
                  data-testid={AD_CREATIVE_BATCH.launchWinnerName(item.id)}
                  title={adName}
                >
                  {adName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatBadge(item)} · {hookLine(item, locale, 'strict')}
                </Typography>
              </Stack>
              <StyledTextField
                select
                label="Deployment language"
                size="small"
                value={locale}
                data-testid={AD_CREATIVE_BATCH.launchWinnerLocale(item.id)}
                onChange={(value) => setLocalesByItemId((current) => ({
                  ...current,
                  [item.id]: String(value),
                }))}
                sx={{ minWidth: 175 }}
              >
                {item.languages.map((candidate) => (
                  <MenuItem key={candidate} value={candidate}>
                    {candidate} · {getCreativeLocaleStatus(item, candidate).replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </StyledTextField>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={fallbackApprovedByItemId[item.id] === true}
                    onChange={(event) => setFallbackApprovedByItemId((current) => ({
                      ...current,
                      [item.id]: event.target.checked,
                    }))}
                  />
                )}
                label={<Typography variant="caption">Allow verified destination-language fallback</Typography>}
              />
            </Stack>
          ))}
        </Stack>
        {localeBlockers.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid={AD_CREATIVE_BATCH.launchLocaleBlocker}>
            {`${localeBlockers.length} selected locale${localeBlockers.length === 1 ? '' : 's'} are not strictly ready. Each Segmently copy must pass creative approval and have approved/source copy, every media slot, and a destination in the exact selected language.`}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
          <StyledTextField
            label="Naming convention"
            size="small"
            fullWidth
            value={namingTemplate}
            data-testid={AD_CREATIVE_BATCH.launchNamingTemplate}
            onChange={(value) => setNamingTemplate(String(value))}
          />
          {namingTemplate !== (projectNaming ?? DEFAULT_NAMING_CONVENTION) && (
            <Button
              size="small"
              data-testid={AD_CREATIVE_BATCH.launchSaveNaming}
              onClick={() => void saveNamingAsProjectDefault()}
              disabled={savingNaming}
            >
              Save as project default
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Destination & tracking</Typography>
        <Stack spacing={1}>
          {namedWinners.map(({ item, locale }) => (
            <Typography key={item.id} variant="caption" color="text.secondary">
              {`${locale}: ${String(item.localizedDestinations?.[locale]?.url ?? 'Exact destination missing')}`}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_BATCH.launchUtmPreview}>
            {`UTM: utm_source=${utm.utmSource} · utm_medium=${utm.utmMedium} · utm_campaign=${utm.utmCampaign} · utm_content={{ad.name}}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Segmently uses each localized copy's exact destination and adds only these tracking parameters. The destination identity cannot be replaced during Meta launch.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Send to Meta</Typography>
          <Chip
            size="small"
            color={connected ? 'success' : 'default'}
            label={connected ? 'Connected' : 'Not connected'}
          />
          {loading && <CircularProgress size={14} />}
        </Stack>

        {!connected && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Connect Meta Ads (from a creative's detail panel or project integrations) to push the batch; the bulk sheet export below works without a connection.
          </Alert>
        )}

        {connected && (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <StyledTextField
                select
                label="Ad account"
                size="small"
                value={accountId}
                data-testid={AD_CREATIVE_BATCH.launchAdAccount}
                onChange={(value) => setAccountId(String(value))}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>{account.name || account.id}</MenuItem>
                ))}
              </StyledTextField>
              <StyledTextField
                select
                label="Page identity"
                size="small"
                value={pageId}
                data-testid={AD_CREATIVE_BATCH.launchPage}
                onChange={(value) => setPageId(String(value))}
              >
                {pages.map((page) => (
                  <MenuItem key={page.id} value={page.id}>{page.name || page.id}</MenuItem>
                ))}
              </StyledTextField>
              <StyledTextField
                select
                label="Campaign"
                size="small"
                value={campaignId}
                data-testid={AD_CREATIVE_BATCH.launchCampaign}
                onChange={(value) => setCampaignId(String(value))}
              >
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>{campaign.name || campaign.id}</MenuItem>
                ))}
              </StyledTextField>
              <Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={adSetMode}
                  data-testid={AD_CREATIVE_BATCH.launchAdSetMode}
                  onChange={(_event, value: AdSetMode | null) => {
                    if (value) setAdSetMode(value);
                  }}
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="new">New paused ad set</ToggleButton>
                  <ToggleButton value="existing">Existing ad set</ToggleButton>
                </ToggleButtonGroup>
                {adSetMode === 'new' ? (
                  <StyledTextField
                    label="New ad set name"
                    size="small"
                    fullWidth
                    value={newAdSetName}
                    data-testid={AD_CREATIVE_BATCH.launchNewAdSetName}
                    onChange={(value) => setNewAdSetName(String(value))}
                  />
                ) : (
                  <StyledTextField
                    select
                    label="Ad set"
                    size="small"
                    fullWidth
                    value={adSetId}
                    data-testid={AD_CREATIVE_BATCH.launchAdSet}
                    onChange={(value) => setAdSetId(String(value))}
                  >
                    {adSets.map((adSet) => (
                      <MenuItem key={adSet.id} value={adSet.id}>{adSet.name || adSet.id}</MenuItem>
                    ))}
                  </StyledTextField>
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
              data-testid={AD_CREATIVE_BATCH.launchSubmit}
              disabled={submitting
                || !accountId || !pageId || !campaignId
                || localeBlockers.length > 0
                || (adSetMode === 'existing' ? !adSetId : !newAdSetName)}
              onClick={() => void submitLaunch()}
            >
              {`Create paused ad set (${winners.length} ads)`}
            </Button>
          </Stack>
        )}

        <Button
          size="small"
          sx={{ mt: 1.5 }}
          startIcon={<DownloadIcon />}
          data-testid={AD_CREATIVE_BATCH.launchBulkSheet}
          disabled={localeBlockers.length > 0}
          onClick={downloadBulkSheet}
        >
          Download Meta bulk sheet (CSV)
        </Button>

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {result && <Alert severity="success" sx={{ mt: 1.5 }} data-testid={AD_CREATIVE_BATCH.launchResult}>{result}</Alert>}
      </Box>
    </Stack>
  );
}

function PreviewThumb({ item, locale }: { item: AdCreativeItem; locale: string }): React.ReactElement {
  const projection = resolveCreativeLocaleProjection(item, locale, 'strict');
  if (projection.previewUrl) {
    return (
      <Box
        component={projection.previewMediaKind === 'video' ? 'video' : 'img'}
        src={projection.previewUrl}
        alt={projection.previewMediaKind === 'video' ? undefined : hookLine(item, locale, 'strict')}
        muted={projection.previewMediaKind === 'video' ? true : undefined}
        sx={{ width: 44, height: 55, objectFit: 'cover', borderRadius: 0.75, border: 1, borderColor: 'divider' }}
      />
    );
  }
  return (
    <Box sx={{ width: 44, height: 55, borderRadius: 0.75, border: 1, borderColor: 'divider', bgcolor: 'grey.100' }} />
  );
}

interface BulkSheetRow {
  adName: string;
  campaignName: string;
  adSetName: string;
  headline: string;
  primaryText: string;
  description: string;
  cta: string;
  landingUrl: string;
  mediaUrl: string;
  format: string;
  locale: string;
}

/**
 * Meta Ads Manager bulk-import column layout (decision #3 secondary path).
 * Media arrives as URLs — Ads Manager import expects uploaded media names, so
 * the sheet doubles as the asset manifest for high-volume teams.
 */
export function buildBulkSheetCsv(rows: BulkSheetRow[]): string {
  const header = [
    'Campaign Name', 'Ad Set Name', 'Ad Name', 'Ad Status', 'Format',
    'Locale', 'Headline', 'Primary Text', 'Description', 'Call to Action', 'Link', 'Media URL',
  ];
  const lines = rows.map((row) => [
    row.campaignName, row.adSetName, row.adName, 'PAUSED', row.format,
    row.locale, row.headline, row.primaryText, row.description, row.cta, row.landingUrl, row.mediaUrl,
  ].map(csvEscape).join(','));
  return [header.join(','), ...lines].join('\n');
}

export function isCreativeLocaleStrictlyLaunchable(item: AdCreativeItem, locale: string): boolean {
  if (!isSegmentlyOwnedCreativeItem(item)) return false;
  if (item.review?.approvalStage !== 'approved') return false;
  const status = getCreativeLocaleStatus(item, locale);
  if (status !== 'source' && status !== 'ready') return false;
  const projection = resolveCreativeLocaleProjection(item, locale, 'strict');
  if (!projection.copy.headline.hasRequestedValue || !projection.copy.primaryText.hasRequestedValue) return false;
  if (Object.values(projection.media).some((entry) => !entry.hasRequestedValue)) return false;
  if (!projection.previewUrl) return false;
  const destinationUrl = item.localizedDestinations?.[locale]?.url;
  try {
    return Boolean(destinationUrl && ['http:', 'https:'].includes(new URL(destinationUrl).protocol));
  } catch {
    return false;
  }
}

function csvEscape(value: string): string {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}
